import { Component, inject, OnInit, effect } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import {
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	NavigationStart,
	Router
} from '@angular/router';
import NProgress from 'nprogress';
import { environment } from 'src/environments/environment';
import { InactivityService } from './services/utils/inactivity.service';
import { SiteStore } from './store/site.store';
import { showMessageWarning } from './utils/utils';
import { QrTransferStore } from './store/qr-transfer.store';
import { QRPaymentEnum } from './store/types';
import { TransferStore } from './store/transfer.store';
import { AccountAccessStore } from './store/account-access.store';
import { ServiceAccessStore } from './store/service-access.store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastService } from './services/toast.service';
import { AuthStore } from './store/auth.store';
import { AuctionService } from './services/auction.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [],
  animations: [],
  hostDirectives: [],
  standalone: false
})
export class AppComponent implements OnInit {
  qrTransfer = inject(QrTransferStore);
	appVersion = environment.appVersion;
  accountAccessStore = inject(AccountAccessStore);
  /** Injected to eagerly initialize the store's effect that reacts to account changes */
  private _serviceAccessStore = inject(ServiceAccessStore);
	matSnackBar = inject(MatSnackBar);
  toastService = inject(ToastService);
  private authStore = inject(AuthStore);
  private auctionService = inject(AuctionService);
  /** Ensures the pending-victory check runs at most once per app session. */
  private victoryChecked = false;

  // Rutas que NO están permitidas para cuentas de solo lectura
  private restrictedRoutesForViewOnly = [
    '/send',
    '/transfer',
    '/own-transfer',
    '/qr-payments'
  ];

  constructor(
    private router: Router,
    private metaService: Meta,
		public siteStore: SiteStore,
		private transferStore: TransferStore,
  ) {
    this.metaService.addTag({ name: 'version', content: environment.appVersion });
		console.log(`%cVersion: ${environment.appVersion}`, 'color:rgb(20, 184, 255); font-size: 20px; font-weight: bold;');
		
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        NProgress.start();
      }
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        NProgress.done();
      }

      if (event instanceof NavigationEnd) {
        // Handle redirects from QR code routes
        this.handleQrRouteRedirects(event.url);
        // If the winner has an unacknowledged auction win, take them to it so the
        // victory dialog pops (the auction page auto-opens + acknowledges it).
        this.maybeShowPendingVictory(event.url);
      }
    });

    if (environment.production) {
      showMessageWarning();
    }

    // Effect para validar rutas cuando cambia la cuenta seleccionada
    effect(() => {
      const isViewOnly = this.accountAccessStore.isViewOnlyAccount();
      
      if (isViewOnly) {
        const currentUrl = this.router.url;
        const isRestricted = this.restrictedRoutesForViewOnly.some(route => 
          currentUrl.startsWith(route)
        );

        if (isRestricted) {
          // Redirigir silenciosamente a home
          this.router.navigate(['/']);
        }
      }
    });
  }

  ngOnInit(): void {
    // Configuración global de toasts
    this.toastService.setGlobalConfig({
      offset: { top: 70 } // 3rem = 48px
    });

    setTimeout(() => {
      const loader = document.getElementById('global-loading');
      if (loader) {
        loader.classList.add('fav1');
        
        setTimeout(() => {
          loader.style.display = 'none';
        }, 1300);
      }
    }, 200);
  }

  /**
   * Once per session, if the authenticated user is the winner of an auction whose
   * victory dialog they haven't acknowledged yet, navigate to that auction so the
   * dialog auto-opens. Skips the login flow and the auction pages (which handle it
   * themselves). Best-effort: a failure just means it retries next session.
   */
  private maybeShowPendingVictory(url: string): void {
    if (this.victoryChecked) return;
    if (!this.authStore.isAuthenticated()) return;
    if (url.startsWith('/login') || url.startsWith('/auctions/')) return;
    this.victoryChecked = true;
    this.auctionService
      .getPendingVictory()
      .then((res: any) => {
        const auction = res?.data;
        if (auction?._id) this.router.navigate(['/auctions', auction._id]);
      })
      .catch(() => {});
  }

  /**
   * Handle QR code route redirects
   * Extracts the route parameters from the original URL and processes the QR payment
   */
  private handleQrRouteRedirects(url: string): void {
    if (url.startsWith('/payto/')) {
      // This is a /payto/:id redirect
      const id = url.split('/')[2];
			this.qrTransfer.manageQrPayment(QRPaymentEnum.ACCOUNT_QR, id);
      return;
    }
    if (url.startsWith('/pay/')) {
      // This is a /pay/:id redirect
      const id = url.split('/')[2];
      this.qrTransfer.manageQrPayment(QRPaymentEnum.GENERATED_QR, id);
      return;
    }
    // if (url.startsWith('/paylink/')) {
    //   // This is a /pay/:id redirect
    //   const id = url.split('/')[2];
    //   this.qrTransfer.manageLinkPayment(id);
    //   return;
    // }
  }
}
